import { withAuthenticationRequired } from "@auth0/auth0-react";
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  Chip,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SpeedIcon from "@mui/icons-material/Speed";
import BarChartIcon from "@mui/icons-material/BarChart";
import ApiIcon from "@mui/icons-material/Api";
import BusinessIcon from "@mui/icons-material/Business";
import SecurityIcon from "@mui/icons-material/Security";
import { CONFIG } from "../config";

const TIER_CONFIG = {
  standard: { label: "Standard", color: "#6b7280", bg: "rgba(107, 114, 128, 0.15)" },
  premium: { label: "Premium", color: "#3b82f6", bg: "rgba(59, 130, 246, 0.15)" },
  enterprise: { label: "Enterprise", color: "#8b5cf6", bg: "rgba(139, 92, 246, 0.15)" }
};

const SCOPE_LABELS = {
  "read:consumption": "Lecture consommation",
  "read:residences": "Lecture résidences",
  "read:establishments": "Lecture établissements",
  "read:devices": "Lecture appareils",
  "write:occupancy": "Écriture occupation"
};

function APIKeyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [apiKey, setApiKey] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchApiKeyDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${CONFIG.API_URL}/admin/api-keys/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        const key = data.find((k) => k.id === parseInt(id));
        if (key) {
          setApiKey(key);
        } else {
          setError("Clé API non trouvée");
        }
      } else {
        setError("Erreur lors du chargement");
      }
    } catch (err) {
      setError("Erreur réseau: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchUsageStats = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${CONFIG.API_URL}/admin/api-keys/${id}/usage?days=7`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (err) {
      // Erreur silencieuse
    }
  }, [id]);

  useEffect(() => {
    fetchApiKeyDetails();
    fetchUsageStats();
  }, [fetchApiKeyDetails, fetchUsageStats]);

  const handleRevoke = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir révoquer la clé API "${apiKey.name}" ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${CONFIG.API_URL}/admin/api-keys/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        navigate("/api-keys");
      } else {
        alert("Erreur lors de la révocation");
      }
    } catch (err) {
      alert("Erreur réseau: " + err.message);
    }
  };

  const handlePermanentDelete = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir SUPPRIMER DÉFINITIVEMENT la clé API "${apiKey.name}" ?\n\nCette action est irréversible.`)) {
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${CONFIG.API_URL}/admin/api-keys/${id}/permanent`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        navigate("/api-keys");
      } else {
        alert("Erreur lors de la suppression");
      }
    } catch (err) {
      alert("Erreur réseau: " + err.message);
    }
  };

  // Formater la date
  const formatDateTime = (dateString) => {
    if (!dateString) return "Jamais";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress sx={{ color: "#26A69A" }} />
      </Box>
    );
  }

  if (error || !apiKey) {
    return (
      <Card sx={{
        backgroundColor: "rgba(40, 50, 45, 0.9)",
        border: "1px solid rgba(239, 68, 68, 0.3)",
        borderRadius: "12px",
        p: 3,
        textAlign: "center"
      }}>
        <CancelIcon sx={{ fontSize: 48, color: "#ef4444", mb: 2 }} />
        <Typography variant="h6" sx={{ color: "#ef4444", mb: 2 }}>
          {error || "Clé API non trouvée"}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/api-keys")}
          sx={{ color: "#26A69A", borderColor: "#26A69A" }}
        >
          Retour aux clés API
        </Button>
      </Card>
    );
  }

  const tierConfig = TIER_CONFIG[apiKey.rate_limit_tier] || TIER_CONFIG.standard;

  return (
    <Grid container spacing={2}>
      {/* Header */}
      <Grid item xs={12}>
        <Card sx={{
          backgroundColor: "rgba(40, 50, 45, 0.9)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "12px",
          overflow: "hidden"
        }}>
          {/* Barre de statut colorée */}
          <Box sx={{
            height: "4px",
            backgroundColor: apiKey.is_active ? "#22c55e" : "#ef4444"
          }} />

          <CardContent sx={{ p: 3 }}>
            {/* Titre et actions */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{
                  backgroundColor: "rgba(38, 166, 154, 0.15)",
                  borderRadius: "12px",
                  p: 1.5,
                  display: "flex"
                }}>
                  <VpnKeyIcon sx={{ fontSize: 32, color: "#26A69A" }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {apiKey.name}
                  </Typography>
                  {apiKey.description && (
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", mt: 0.5 }}>
                      {apiKey.description}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/api-keys")}
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    borderColor: "rgba(255,255,255,0.2)",
                    "&:hover": { borderColor: "rgba(255,255,255,0.4)" }
                  }}
                >
                  Retour
                </Button>
                {apiKey.is_active ? (
                  <Button
                    variant="contained"
                    startIcon={<DeleteIcon />}
                    onClick={handleRevoke}
                    sx={{
                      backgroundColor: "#ef4444",
                      "&:hover": { backgroundColor: "#dc2626" }
                    }}
                  >
                    Révoquer
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<DeleteForeverIcon />}
                    onClick={handlePermanentDelete}
                    sx={{
                      backgroundColor: "#ef4444",
                      "&:hover": { backgroundColor: "#dc2626" }
                    }}
                  >
                    Supprimer définitivement
                  </Button>
                )}
              </Box>
            </Box>

            {/* Informations principales */}
            <Grid container spacing={3}>
              {/* Colonne gauche */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  {/* Key ID */}
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mb: 0.5 }}>
                      Identifiant de clé
                    </Typography>
                    <Box sx={{
                      backgroundColor: "rgba(0,0,0,0.3)",
                      borderRadius: "6px",
                      p: 1,
                      fontFamily: "monospace",
                      fontSize: "0.9rem",
                      color: "#26A69A"
                    }}>
                      {apiKey.key_id}
                    </Box>
                  </Box>

                  {/* Établissement */}
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mb: 0.5 }}>
                      <BusinessIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                      Établissement
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {apiKey.establishment_name}
                    </Typography>
                  </Box>

                  {/* Marque */}
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mb: 0.5 }}>
                      Marque
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {apiKey.brand_name}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Colonne droite */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  {/* Environnement */}
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mb: 0.5 }}>
                      Environnement
                    </Typography>
                    <Chip
                      label={apiKey.environment === "live" ? "PRODUCTION" : "TEST"}
                      size="small"
                      sx={{
                        backgroundColor: apiKey.environment === "live"
                          ? "rgba(34, 197, 94, 0.15)"
                          : "rgba(251, 191, 36, 0.15)",
                        color: apiKey.environment === "live" ? "#22c55e" : "#fbbf24",
                        fontWeight: 600,
                        border: `1px solid ${apiKey.environment === "live" ? "#22c55e" : "#fbbf24"}`,
                      }}
                    />
                  </Box>

                  {/* Statut */}
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mb: 0.5 }}>
                      Statut
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {apiKey.is_active ? (
                        <>
                          <CheckCircleIcon sx={{ fontSize: 18, color: "#22c55e" }} />
                          <Typography sx={{ color: "#22c55e", fontWeight: 500 }}>Actif</Typography>
                        </>
                      ) : (
                        <>
                          <CancelIcon sx={{ fontSize: 18, color: "#ef4444" }} />
                          <Typography sx={{ color: "#ef4444", fontWeight: 500 }}>Révoqué</Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Niveau de quota */}
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mb: 0.5 }}>
                      <SpeedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                      Niveau de quota
                    </Typography>
                    <Chip
                      label={`${tierConfig.label} (${apiKey.requests_per_day?.toLocaleString() || "10 000"}/jour)`}
                      size="small"
                      sx={{
                        backgroundColor: tierConfig.bg,
                        color: tierConfig.color,
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Box>
              </Grid>

              {/* Permissions - pleine largeur */}
              <Grid item xs={12}>
                <Box sx={{
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "8px",
                  p: 2,
                  mt: 1
                }}>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "flex", alignItems: "center", mb: 1.5 }}>
                    <SecurityIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Permissions
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {apiKey.scopes.map((scope) => (
                      <Chip
                        key={scope}
                        label={SCOPE_LABELS[scope] || scope}
                        size="small"
                        sx={{
                          backgroundColor: scope.startsWith("read:")
                            ? "rgba(59, 130, 246, 0.15)"
                            : "rgba(249, 115, 22, 0.15)",
                          color: scope.startsWith("read:") ? "#3b82f6" : "#f97316",
                          fontSize: "0.75rem",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>

              {/* Dates */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AccessTimeIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.4)" }} />
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                    Créé le: <span style={{ color: "rgba(255,255,255,0.8)" }}>{formatDateTime(apiKey.created_at)}</span>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AccessTimeIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.4)" }} />
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                    Dernière utilisation: <span style={{ color: "rgba(255,255,255,0.8)" }}>{formatDateTime(apiKey.last_used_at)}</span>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Statistiques d'utilisation */}
      {usage && (
        <>
          {/* Carte Total Requêtes */}
          <Grid item xs={12} md={4}>
            <Card sx={{
              backgroundColor: "rgba(40, 50, 45, 0.9)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderTop: "3px solid #26A69A",
              borderRadius: "12px",
              height: "100%"
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <BarChartIcon sx={{ color: "#26A69A", fontSize: 20 }} />
                  <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem" }}>
                    Requêtes (7 jours)
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "2.5rem", fontWeight: 700, color: "#26A69A" }}>
                  {usage.total_requests.toLocaleString("fr-FR")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Carte Temps de réponse */}
          <Grid item xs={12} md={4}>
            <Card sx={{
              backgroundColor: "rgba(40, 50, 45, 0.9)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderTop: "3px solid #22c55e",
              borderRadius: "12px",
              height: "100%"
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <SpeedIcon sx={{ color: "#22c55e", fontSize: 20 }} />
                  <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem" }}>
                    Temps de réponse moyen
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "2.5rem", fontWeight: 700, color: "#22c55e" }}>
                  {Math.round(usage.average_response_time_ms)} <span style={{ fontSize: "1.2rem" }}>ms</span>
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Carte Codes de statut */}
          <Grid item xs={12} md={4}>
            <Card sx={{
              backgroundColor: "rgba(40, 50, 45, 0.9)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderTop: "3px solid #3b82f6",
              borderRadius: "12px",
              height: "100%"
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <ApiIcon sx={{ color: "#3b82f6", fontSize: 20 }} />
                  <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem" }}>
                    Codes de statut
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {Object.entries(usage.status_codes).map(([code, count]) => (
                    <Chip
                      key={code}
                      label={`${code}: ${count.toLocaleString("fr-FR")}`}
                      size="small"
                      sx={{
                        backgroundColor: code.startsWith("2")
                          ? "rgba(34, 197, 94, 0.15)"
                          : "rgba(239, 68, 68, 0.15)",
                        color: code.startsWith("2") ? "#22c55e" : "#ef4444",
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Top Endpoints */}
          <Grid item xs={12}>
            <Card sx={{
              backgroundColor: "rgba(40, 50, 45, 0.9)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "12px",
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <ApiIcon sx={{ color: "#26A69A", fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>
                    Top Endpoints (7 derniers jours)
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {Object.entries(usage.requests_by_endpoint)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 10)
                    .map(([endpoint, count], index) => {
                      const maxCount = Math.max(...Object.values(usage.requests_by_endpoint));
                      const percentage = (count / maxCount) * 100;
                      return (
                        <Box key={endpoint} sx={{ position: "relative" }}>
                          <Box sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                            px: 1.5,
                            borderRadius: "6px",
                            backgroundColor: "rgba(0,0,0,0.2)",
                            position: "relative",
                            overflow: "hidden"
                          }}>
                            {/* Barre de progression en arrière-plan */}
                            <Box sx={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: `${percentage}%`,
                              backgroundColor: "rgba(38, 166, 154, 0.15)",
                              transition: "width 0.3s ease"
                            }} />
                            <Typography sx={{
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                              color: "rgba(255,255,255,0.9)",
                              position: "relative",
                              zIndex: 1
                            }}>
                              {endpoint}
                            </Typography>
                            <Chip
                              label={count.toLocaleString("fr-FR")}
                              size="small"
                              sx={{
                                backgroundColor: "rgba(38, 166, 154, 0.2)",
                                color: "#26A69A",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                position: "relative",
                                zIndex: 1
                              }}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </>
      )}
    </Grid>
  );
}

export default withAuthenticationRequired(APIKeyDetails);
